import get from 'lodash/get';

import { findMatchedRouteConfig } from 'Lib/utils/linkUtils';
import shallowEqual from 'Lib/utils/shallowEqual';
import shallowEqualNested from 'Lib/utils/shallowEqualNested';
import Routes from 'Routes';

import { appInitializeComplete } from 'Store/lifecycles';
import { getIsAppLoading } from 'Store/loading/selectors';
import { setRouteEnd } from 'Store/routing/actions';
import {
  getCurrentPath,
  getPathName,
  getPreviousPath,
  getRouteParams,
  getUrlSearch,
} from 'Store/routing/selectors';
import { getIsUserLoggedIn } from 'Store/user/selectors';

// props has deep json structure (or include random key) which will result shallow compare return false everytime
const ignoredProps: string[] = ['routeConfig', 'routeParams', 'render'];

type CustomizedComponentType = React.ComponentType<any> & {
  preload?: () => Promise<any>;
};

export const areEqual = (prevProps: any, nextProps: any) =>
  shallowEqualNested(prevProps, nextProps, ignoredProps, false) &&
  shallowEqual(prevProps.routeParams, nextProps.routeParams);

export const getRegionConfig = (
  region: string,
  config: any
): {
  component: CustomizedComponentType;
  animation: number;
  props?: any;
} => ({
  component: get(config, ['regions', region]),
  animation: get(config, ['animation', region]),
  props: get(config, ['props', region]),
});

export const mapStateToProps = (state: any) => {
  const pathname = getPathName(state);
  const matchedRouteConfig = findMatchedRouteConfig(pathname, Routes);

  return {
    currentPath: getCurrentPath(state),
    hasPrevPath: !!getPreviousPath(state),
    initialized: appInitializeComplete.isTriggered(state),
    loading: getIsAppLoading(state),
    loggedIn: getIsUserLoggedIn(state),
    pathname,
    routeConfig: matchedRouteConfig.routeConfig,
    routeParams: getRouteParams(state),
    search: getUrlSearch(state),
  };
};

export const mapDispatchToProps = {
  setRouteEnd,
};

export type RouteConfigType = {
  path?: string;
  authed?: boolean;
  hasEncasedLandmarks?: boolean;
  ignoreAuthCheck?: boolean;
  onEnter?: (context: any) => (a: any) => void;
  prefetch?: {
    preload: VoidFunction;
  }[];
  mobileBottomNav?: React.ReactElement;
  mobileTopNav?: React.ReactElement;
  showMobileBackground?: boolean;
  regions?: {
    canvas: CustomizedComponentType;
    main: CustomizedComponentType;
    side: CustomizedComponentType;
  };
  animation?: {
    main: number;
    side: number;
    canvas: number;
  };
  props?: {
    main?: Record<string, any>;
    side?: Record<string, any>;
    canvas?: Record<string, any>;
  };
  navbarPosition?: string;
  pageMeta?: {
    titleIntlId?: string;
  };
};

export type CommonProps = {
  currentPath: string;
  hasPrevPath: boolean;
  initialized: boolean;
  loading: boolean;
  loggedIn: boolean;
  routeParams: Record<string, any>;
  pathname: string;
  routeConfig: RouteConfigType | undefined;
  search?: string | null;
  setRouteEnd?: VoidFunction;
};

export const getKeyName = (component: any, fallback?: string) => component?.displayName ?? fallback;
